import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useSelector } from 'react-redux';
import { useGetProfilesPinDataForDefaultPointQuery } from '../../features/profilesSlice';
import { GetUserTypePinPNG } from '../markers/MMMarkers';

// The four map pin TYPES, in display order (left→right): Caregiver, Medical professional, Other, Patient.
// Patient is last so it renders rightmost — and, since each icon overlaps the previous, on top / most visible.
const PIN_TYPES = ['C', 'M', 'O', 'P'];

// Total pins on the map — a frosted, semi-transparent "map stat" pill, top-center over the map area.
// Reuses the same cached count_total query the floating cards already use (no new endpoint). The icon row is the
// four pin-type markers overlapped ~30% to save space; each icon is resolved through GetUserTypePinPNG — the SAME
// resolver the markers use — so the disease-specific pin art (patienticon<disease>.png, etc.) matches the map.
export const MapPinCount: React.FC = () => {
  const { data } = useGetProfilesPinDataForDefaultPointQuery();
  const searchExpanded = useSelector((s: any) => s.dialog.searchExpanded);
  const total = Number(data?.[0]?.count_total); // pg int8 arrives as a string → coerce
  // Hide while the full-width search bar is open (it would overlap the top-center pill), or when there's no count.
  if (!total || searchExpanded) return null;

  const icons = PIN_TYPES.map((t) => GetUserTypePinPNG(t) as string | null).filter(Boolean) as string[];

  return (
    <Box
      sx={{
        position: 'absolute',
        top: '72px', // same band as the search/zoom controls, below the 64px navbar
        zIndex: 1,
        pointerEvents: 'none',
        display: 'flex',
        justifyContent: 'center', // center the pill within the band below
        // Center within the band between the map's LEFT EDGE and the top-right search button — NOT the whole map.
        // left = map's left edge (clears the ~360px card column on md; ~10px on mobile);
        // right leaves room for the search + filter buttons at the top-right.
        left: { xs: '10px', md: '376px' },
        right: '100px',
      }}
    >
      <Paper
        elevation={0}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          pl: 0.5,
          pr: 1,
          height: 32,
          borderRadius: 5,
          pointerEvents: 'auto',
          backgroundColor: 'rgba(244, 240, 255, 0.3)', // translucent middle ground
          backdropFilter: 'blur(6px)', // light blur for legibility — not the heavy 24px that read as opaque
          WebkitBackdropFilter: 'blur(6px)',
          border: '1px solid #656EFF', // same color as the font
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.06)',
        }}
      >
        {/* Overlapping pin-type icons (~30% shift) */}
        <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
          {icons.map((src, i) => (
            <Box
              key={i}
              component="img"
              src={src}
              alt=""
              sx={{
                width: 22,
                height: 22,
                objectFit: 'contain',
                ml: i === 0 ? 0 : '-13px', // ~60% overlap of the 22px icon
                filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.15))',
              }}
            />
          ))}
        </Box>
        <Typography sx={{ color: '#656EFF', fontSize: 14, fontWeight: 500, whiteSpace: 'nowrap' }}>
          {total.toLocaleString()} user pins on map
        </Typography>
      </Paper>
    </Box>
  );
};
